import { ReactNode, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { CCMSProvider, AuthProvider, useHttpClient } from '@actschurch/ccms-sdk';
import { BrowserRouter } from 'react-router-dom';
import { theme } from '@/theme/theme';
import {
  CCMS_BASE_URL,
  CCMS_SERVICE_PATH_RESOURCES,
  CCMS_SERVICE_PATH_AI,
} from '@/config/ccms';
import { createSecureStorage } from '@/utils/secureStorage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

/**
 * Wraps the SDK <c>AuthProvider</c>. Has to live inside <c>CCMSProvider</c> so it
 * can read the <c>HttpClient</c>. Mirrors the ccms_v2 pattern.
 */
function AppWithAuth({ children }: { children: ReactNode }) {
  const httpClient = useHttpClient();
  const storage = useMemo(() => createSecureStorage(), []);
  return (
    <AuthProvider
      httpClient={httpClient}
      storage={storage}
      autoRefresh
      refreshThresholdSeconds={60}
      onLogin={(user) => console.log('[Auth] login', user.email)}
      onLogout={() => console.log('[Auth] logout')}
      onError={(err) => console.error('[Auth] error', err)}
    >
      {children}
    </AuthProvider>
  );
}

/**
 * Root providers for the Resource Centre.
 * - <c>CCMSProvider</c> wires the HttpClient + all SDK services (resources,
 *   knowledge base, analytics, members).
 * - <c>AppWithAuth</c> wires the SDK's <c>AuthProvider</c> + secure storage so
 *   the SDK can persist + auto-refresh tokens (matches ccms_v2 wiring).
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <CCMSProvider
          config={{ baseUrl: CCMS_BASE_URL }}
          services={{
            users: { servicePath: 'users' },
            members: { servicePath: 'members' },
            departments: { servicePath: 'departments' },
            resources: { servicePath: CCMS_SERVICE_PATH_RESOURCES },
            knowledgeBase: { servicePath: CCMS_SERVICE_PATH_RESOURCES },
            knowledgeBaseQA: { servicePath: CCMS_SERVICE_PATH_AI },
            analytics: { servicePath: CCMS_SERVICE_PATH_RESOURCES },
          }}
        >
          <AppWithAuth>
            <MantineProvider theme={theme} defaultColorScheme="auto">
              <ModalsProvider>
                <Notifications position="top-right" />
                {children}
              </ModalsProvider>
            </MantineProvider>
          </AppWithAuth>
        </CCMSProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
